import React, { useEffect, useRef, useState } from 'react';
import { useBiases } from '../../hooks/useApi';
import { CognitiveBias } from '../../types';
import { RotateCcw, ZoomIn, ZoomOut, RefreshCw, FilterX } from 'lucide-react';
import { Link } from 'react-router-dom';
import * as d3 from 'd3';
import { SearchInput } from '../search/SearchInput';

// Category colors - match the example colors
const categoryColors: Record<string, string> = {
  "decision-making": "#2EBFB3",
  "social": "#E94F96",
  "memory": "#9C6ADE",
  "reasoning": "#5BBA6F",
  "perceptual": "#F5A623"
};

// Category names
const categoryNames: Record<string, string> = {
  "decision-making": "Decision-Making",
  "social": "Social",
  "memory": "Memory",
  "reasoning": "Reasoning",
  "perceptual": "Perceptual"
};

// Category descriptions
const categoryDescriptions: Record<string, string> = {
  "decision-making": "These biases affect how we evaluate options and make choices. They can lead us to make irrational decisions based on how information is presented, our prior commitments, or emotional attachments rather than objective facts.",
  "social": "Social biases influence how we perceive and interact with others. They affect our judgments about people's intentions, abilities, and character, often creating unfair or inaccurate impressions of individuals and groups.",
  "memory": "Memory biases distort how we store, recall, and use information from our past experiences. They can make certain memories seem more accurate or significant than they actually were.",
  "reasoning": "These biases impact our ability to evaluate evidence and arguments logically. They can lead us to reach conclusions that align with our existing beliefs rather than what the evidence actually supports.",
  "perceptual": "Perceptual biases affect how we interpret sensory information. They can cause us to pay attention to certain details while overlooking others, creating a subjective reality that may not match objective events."
};

// Fallback data in case API fails
const fallbackData: CognitiveBias[] = [
  // Decision-making biases
  {
    id: "anchoring-bias",
    name: "Anchoring Bias",
    category: "decision-making",
    short_description: "Our tendency to fixate on the first piece of information we encounter.",
    long_description: "Anchoring bias is the human tendency to rely too heavily on the first piece of information we receive (the \"anchor\") when making decisions. Once an anchor is set, subsequent judgments are made by adjusting away from that anchor, and there is a bias toward interpreting other information around the anchor.",
    related: ["confirmation-bias", "status-quo-bias"]
  },
  {
    id: "availability-heuristic",
    name: "Availability Heuristic",
    category: "decision-making",
    short_description: "Our tendency to rely on information that comes to mind quickly and easily.",
    long_description: "The availability heuristic is a mental shortcut that relies on immediate examples that come to mind when evaluating a specific topic, concept, method or decision. People tend to place greater emphasis on information that is easier to recall, making recent or memorable events seem more probable than they actually are.",
    related: ["recency-bias", "attentional-bias"]
  },
  {
    id: "confirmation-bias",
    name: "Confirmation Bias",
    category: "decision-making",
    short_description: "We tend to seek out and interpret information in ways that support our existing beliefs.",
    long_description: "Confirmation bias is the tendency to search for, interpret, favor, and recall information in a way that confirms or supports one's prior beliefs or values. People display this bias when they select information that supports their views, ignoring contrary information, or when they interpret ambiguous evidence as supporting their existing attitudes.",
    related: ["selective-perception", "belief-bias"]
  },
  {
    id: "status-quo-bias",
    name: "Status Quo Bias",
    category: "decision-making",
    short_description: "Our innate preference for keeping things as they are.",
    long_description: "Our innate preference for keeping things as they are often overpowers the potential benefits of change. We'll stick with familiar options even when alternatives offer significant advantages.",
    related: ["anchoring-bias", "loss-aversion"]
  },
  {
    id: "sunk-cost-fallacy",
    name: "Sunk Cost Fallacy",
    category: "decision-making",
    short_description: "We irrationally cling to choices we've already invested in.",
    long_description: "We irrationally cling to choices we've already invested in—whether time, money, or effort—despite diminishing returns. This powerful psychological trap keeps us throwing good resources after bad.",
    related: ["loss-aversion", "commitment-bias"]
  },
  {
    id: "survivorship-bias",
    name: "Survivorship Bias",
    category: "decision-making",
    short_description: "We tend to focus on successes while ignoring failures.",
    long_description: "We tend to focus on successes while ignoring failures, leading to distorted conclusions. This bias can make risky ventures seem safer than they actually are.",
    related: ["selection-bias", "optimism-bias"]
  },
  
  // Social biases
  {
    id: "bandwagon-effect",
    name: "Bandwagon Effect",
    category: "social",
    short_description: "We tend to adopt beliefs or behaviors because many others do the same.",
    long_description: "We tend to adopt beliefs or behaviors simply because many others do the same. This conformity impulse can override our independent judgment, causing us to make decisions based on social proof rather than personal evaluation.",
    related: ["groupthink", "conformity-bias"]
  },
  {
    id: "fundamental-attribution-error",
    name: "Fundamental Attribution Error",
    category: "social",
    short_description: "We blame others' personality flaws for their actions, yet rarely consider situational factors.",
    long_description: "We're quick to blame others' personality flaws for their actions, yet we rarely consider the situational pressures they face. This bias leads to unfair judgments and missed opportunities for empathy.",
    related: ["self-serving-bias", "actor-observer-bias"]
  },
  {
    id: "false-consensus-effect",
    name: "False Consensus Effect",
    category: "social",
    short_description: "We often assume our views and preferences are widely shared.",
    long_description: "We often assume our views, preferences, and behaviors are widely shared when they're actually minority opinions. This blind spot can lead to serious missteps in business, relationships, and strategic planning.",
    related: ["projection-bias", "in-group-bias"]
  },
  {
    id: "halo-effect",
    name: "Halo Effect",
    category: "social",
    short_description: "Our overall impression of a person influences how we feel about their character.",
    long_description: "The halo effect is a type of cognitive bias in which our overall impression of a person influences how we feel and think about their character. Essentially, our impression of someone influences our evaluations of that person's specific traits. For example, if someone appears attractive, we might also assume they are kind, intelligent, and trustworthy.",
    related: ["confirmation-bias", "in-group-bias"]
  },
  {
    id: "in-group-bias",
    name: "In-Group Bias",
    category: "social",
    short_description: "Our tendency to favor people from our own social circles.",
    long_description: "Our tendency to favor people from our own social circles creates invisible barriers. This preferential treatment of 'insiders' can undermine diversity and lead to flawed decision-making.",
    related: ["stereotyping", "out-group-homogeneity-bias"]
  },
  {
    id: "self-serving-bias",
    name: "Self-Serving Bias",
    category: "social",
    short_description: "We readily take credit for successes but blame external factors for failures.",
    long_description: "We readily take credit for our successes but blame external factors for our failures. This self-protective tendency preserves our self-image while distorting our ability to learn and grow.",
    related: ["fundamental-attribution-error", "actor-observer-bias"]
  },
  {
    id: "dunning-kruger-effect",
    name: "Dunning-Kruger Effect",
    category: "social",
    short_description: "People with limited knowledge or competence overestimate their abilities.",
    long_description: "The Dunning-Kruger effect is a cognitive bias in which people with limited knowledge or competence in a given intellectual or social domain greatly overestimate their competence. This bias results from an internal illusion in people of low ability and from an external misperception in people of high ability.",
    related: ["overconfidence-bias", "illusory-superiority"]
  },
  
  // Memory biases
  {
    id: "context-effect",
    name: "Context Effect",
    category: "memory",
    short_description: "Our memories and judgments are shaped by surrounding circumstances.",
    long_description: "Our memories and judgments are profoundly shaped by surrounding circumstances. What we recall—and how we interpret information—depends heavily on the environment, timing, and emotional state present when we learn or retrieve it.",
    related: ["mood-congruent-memory-bias", "misinformation-effect"]
  },
  {
    id: "false-memory",
    name: "False Memory",
    category: "memory",
    short_description: "Our minds often fabricate or distort memories.",
    long_description: "Our minds often fabricate or distort memories, creating vivid recollections of events that never happened or occurred differently. These false memories feel just as real as genuine ones, making them particularly deceptive.",
    related: ["misinformation-effect", "source-confusion"]
  },
  {
    id: "hindsight-bias",
    name: "Hindsight Bias",
    category: "memory",
    short_description: "We often claim events were predictable after they've happened.",
    long_description: "We often claim events were predictable after they've happened. That nagging feeling of 'I knew it all along' can distort our memory of uncertainty and lead us to overestimate our forecasting abilities.",
    related: ["outcome-bias", "rosy-retrospection"]
  },
  {
    id: "misinformation-effect",
    name: "Misinformation Effect",
    category: "memory",
    short_description: "When exposed to misleading information, we often incorporate these false details into our memories.",
    long_description: "Our memories are surprisingly fragile. When exposed to misleading information after an event, we often unknowingly incorporate these false details into our original memories, creating confident but inaccurate recollections.",
    related: ["false-memory", "source-confusion"]
  },
  {
    id: "recency-bias",
    name: "Recency Bias",
    category: "memory",
    short_description: "We tend to remember and heavily weigh the most recent information we encounter.",
    long_description: "We tend to remember and heavily weigh the most recent information we encounter, often at the expense of earlier details. This can significantly skew our decisions, especially when evaluating complex situations over time.",
    related: ["availability-heuristic", "primacy-effect"]
  },
  {
    id: "rosy-retrospection",
    name: "Rosy Retrospection",
    category: "memory",
    short_description: "Our minds paint the past with a deceptively positive brush.",
    long_description: "Our minds paint the past with a deceptively positive brush, minimizing struggles and amplifying joys. This selective memory affects how we evaluate current situations and make future plans.",
    related: ["nostalgia-bias", "hindsight-bias"]
  },
  
  // Reasoning biases
  {
    id: "backfire-effect",
    name: "Backfire Effect",
    category: "reasoning",
    short_description: "When presented with contradictory evidence, we often strengthen our original beliefs.",
    long_description: "When presented with evidence that contradicts our beliefs, we often reject it and strengthen our original position. This cognitive response transforms corrections into reinforcement of misinformation.",
    related: ["confirmation-bias", "belief-perseverance"]
  },
  {
    id: "base-rate-fallacy",
    name: "Base Rate Fallacy",
    category: "reasoning",
    short_description: "We're drawn to specific details while ignoring broader statistical realities.",
    long_description: "We're naturally drawn to specific, vivid details while ignoring broader statistical realities. This mental blindspot leads us to overestimate unlikely events and make poor probability judgments in everything from medical decisions to risk assessment.",
    related: ["availability-heuristic", "representativeness-heuristic"]
  },
  {
    id: "belief-bias",
    name: "Belief Bias",
    category: "reasoning",
    short_description: "We judge arguments based on how well they align with our existing beliefs.",
    long_description: "We're wired to judge arguments based on how well they align with our existing beliefs, not by their actual logical merit. This mental shortcut can blind us to valid evidence that challenges our worldview.",
    related: ["confirmation-bias", "motivated-reasoning"]
  },
  {
    id: "framing-effect",
    name: "Framing Effect",
    category: "reasoning",
    short_description: "How information is presented affects our choices, even when the facts are identical.",
    long_description: "The way information is presented—positively or negatively—can dramatically affect our choices, even when the facts are identical. It's why '90% fat-free' sounds better than '10% fat' on food labels.",
    related: ["loss-aversion", "cognitive-dissonance"]
  },
  {
    id: "gamblers-fallacy",
    name: "Gambler's Fallacy",
    category: "reasoning",
    short_description: "The mistaken belief that past events affect the probability of future random events.",
    long_description: "The mistaken belief that if something happens more frequently than normal during a given period, it will happen less frequently in the future—or vice versa—despite statistical independence.",
    related: ["hot-hand-fallacy", "clustering-illusion"]
  },
  {
    id: "planning-fallacy",
    name: "Planning Fallacy",
    category: "reasoning",
    short_description: "We consistently underestimate how long projects will take and how much they'll cost.",
    long_description: "We consistently underestimate how long projects will take and how much they'll cost—even when we have plenty of past evidence showing our optimism is unrealistic. This rosy outlook sets us up for missed deadlines and budget overruns.",
    related: ["optimism-bias", "overconfidence-effect"]
  },
  
  // Perceptual biases
  {
    id: "attentional-bias",
    name: "Attentional Bias",
    category: "perceptual",
    short_description: "Our minds naturally gravitate toward emotionally charged information.",
    long_description: "Our minds naturally gravitate toward emotionally charged information, causing us to fixate on certain details while completely missing other critical data that doesn't trigger the same emotional response.",
    related: ["availability-heuristic", "selective-perception"]
  },
  {
    id: "blind-spot-bias",
    name: "Blind Spot Bias",
    category: "perceptual",
    short_description: "We recognize cognitive biases in others but not in ourselves.",
    long_description: "We readily recognize cognitive biases in others while remaining oblivious to the same biases in ourselves. This meta-bias creates a dangerous illusion of objectivity that can undermine our decision-making quality.",
    related: ["self-serving-bias", "naive-realism"]
  },
  {
    id: "illusory-correlation",
    name: "Illusory Correlation",
    category: "perceptual",
    short_description: "We instinctively connect unrelated events, creating patterns where none exist.",
    long_description: "We instinctively connect unrelated events, creating patterns where none exist. This mental shortcut leads us to see relationships between random occurrences, influencing decisions based on coincidences rather than facts.",
    related: ["apophenia", "clustering-illusion"]
  },
  {
    id: "negativity-bias",
    name: "Negativity Bias",
    category: "perceptual",
    short_description: "Our brains react more strongly to negative experiences than positive ones.",
    long_description: "Our brains are wired to react more strongly to negative experiences than positive ones. This skewed attention means one criticism can outweigh ten compliments, affecting everything from mood to major decisions.",
    related: ["loss-aversion", "pessimism-bias"]
  },
  {
    id: "selective-perception",
    name: "Selective Perception",
    category: "perceptual",
    short_description: "We filter information through the lens of our existing beliefs.",
    long_description: "We naturally filter information through the lens of our existing beliefs, focusing on what confirms our views while conveniently overlooking contradictory evidence—even when it's right in front of us.",
    related: ["confirmation-bias", "attentional-bias"]
  }
];

interface BiasNode {
  id: string;
  name: string;
  x: number;
  y: number;
  category: string;
  color: string;
  isCategoryNode?: boolean;
}

interface CategoryNode {
  id: string;
  name: string;
  x: number;
  y: number;
  color: string;
}

interface BiasNetworkGraphProps {
  selectedCategory?: string;
}

const BiasNetworkGraph: React.FC<BiasNetworkGraphProps> = ({ selectedCategory: initialCategory = 'all' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedBias, setSelectedBias] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  
  // Update selectedCategory when initialCategory changes
  useEffect(() => {
    if (initialCategory && initialCategory !== 'all') {
      setSelectedCategory(initialCategory);
    }
  }, [initialCategory]);
  
  // Add state for sidebar visibility
  const [sidebarVisible, setSidebarVisible] = useState(true);
  
  // Custom zoom state
  const [zoomScale, setZoomScale] = useState(1);
  const [zoomTranslate, setZoomTranslate] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Get biases data
  const { data, isLoading, isError } = useBiases();
  
  // Reference to utility functions that will be set in useEffect
  const utilsRef = useRef<{
    highlightRelatedBiases: (biasId: string) => void;
    resetHighlighting: () => void;
  }>({
    highlightRelatedBiases: () => {},
    resetHighlighting: () => {},
  });
  
  // Validate and normalize bias data to ensure it has required fields
  const validateBias = (bias: any): CognitiveBias => {
    // Make sure we have a valid object
    if (!bias || typeof bias !== 'object') {
      console.warn('Invalid bias data:', bias);
      return {
        id: `unknown-${Math.random().toString(36).substring(2, 9)}`,
        name: "Unknown Bias",
        category: "decision-making",
        short_description: "No description available",
        long_description: "",
        related: []
      };
    }

    // Ensure required fields are present
    return {
      id: bias.id || `bias-${Math.random().toString(36).substring(2, 9)}`,
      name: bias.name || "Unnamed Bias",
      category: typeof bias.category === 'string' 
        ? bias.category.toLowerCase().replace(/\s+/g, '-')
        : "decision-making",
      short_description: bias.short_description || bias.description || "No description available",
      long_description: bias.long_description || bias.description || "",
      related: Array.isArray(bias.related) ? bias.related : []
    };
  };

  // Use fallback data if API fails or returns empty
  const biasesData = data?.pages.flatMap(page => page.biases) || [];
  const useStaticData = isError || biasesData.length === 0;
  const allBiases = (useStaticData ? fallbackData : biasesData).map(validateBias);

  // Debug log data
  useEffect(() => {
    if (!isLoading) {
      console.log('Biases Data:', { 
        useStaticData, 
        allBiasesCount: allBiases.length,
        sampleBias: allBiases[0] 
      });
    }
  }, [isLoading, allBiases, useStaticData]);

  // Define applyFilters function before any useEffect that might use it
  const applyFilters = () => {
    if (!svgRef.current) return;
    
    // Save current zoom transform before applying filters
    const g = d3.select(svgRef.current).select('.everything');
    const currentScale = g.attr('data-zoom-scale') || zoomScale;
    const currentX = g.attr('data-zoom-x') || zoomTranslate.x;
    const currentY = g.attr('data-zoom-y') || zoomTranslate.y;
    
    // Get D3 selections
    const svg = d3.select(svgRef.current);
    
    // Create category groups
    const categories = Object.keys(categoryNames).map(id => ({
      id,
      name: categoryNames[id],
      color: categoryColors[id]
    }));
    
    // Filter by category
    if (selectedCategory !== 'all') {
      // Highlight the selected category
      categories.forEach(category => {
        if (category.id !== selectedCategory) {
          // Dim non-selected categories - increased opacity reduction
          svg.select(`#category-${category.id}`).style('opacity', 0.15);
          svg.select(`#label-${category.id}`).style('opacity', 0.15);
          svg.select(`#label-bg-${category.id}`).style('opacity', 0.15);
          svg.select(`#link-center-to-${category.id}`).style('opacity', 0.05);
          
          // Dim bias nodes for non-selected categories - increased opacity reduction
          svg.selectAll(`.${category.id}-bias`).style('opacity', 0.15);
          svg.selectAll(`.${category.id}-label`).style('opacity', 0.15);
          svg.selectAll(`.${category.id}-label-bg`).style('opacity', 0.15);
          svg.selectAll(`.${category.id}-link`).style('opacity', 0.05);
        } else {
          // Highlight selected category
          svg.select(`#category-${category.id}`)
            .style('opacity', 1)
            .style('stroke-width', 3);
          svg.select(`#label-${category.id}`).style('opacity', 1);
          svg.select(`#label-bg-${category.id}`).style('opacity', 1);
          svg.select(`#link-center-to-${category.id}`)
            .style('opacity', 0.9)
            .style('stroke-width', 4);
          
          // Highlight all bias nodes for selected category
          svg.selectAll(`.${category.id}-bias`)
            .style('opacity', 1)
            .style('stroke-width', 2);
          svg.selectAll(`.${category.id}-label`).style('opacity', 1);
          svg.selectAll(`.${category.id}-label-bg`).style('opacity', 1);
          svg.selectAll(`.${category.id}-link`)
            .style('opacity', 0.8)
            .style('stroke-width', 2);
        }
      });
      
      // Hide related links between different categories
      svg.selectAll('.related-link').style('opacity', 0.05);
    } else if (selectedBias) {
      // If a specific bias is selected, highlight it and its related biases
      // First dim everything - increased opacity reduction for stronger blur effect
      svg.selectAll('.bias-node').style('opacity', 0.2);
      svg.selectAll('.bias-label').style('opacity', 0.2);
      svg.selectAll('.bias-label-bg').style('opacity', 0.2);
      svg.selectAll('.category-node').style('opacity', 0.2);
      svg.selectAll('.category-label').style('opacity', 0.2);
      svg.selectAll('.category-label-bg').style('opacity', 0.2);
      svg.selectAll('.related-link').style('opacity', 0.05);
      svg.selectAll('.bias-link').style('opacity', 0.05);
      
      // Get selected bias data
      const bias = allBiases.find(b => b.id === selectedBias);
      if (bias) {
        // Highlight selected bias
        svg.select(`#bias-${selectedBias}`)
          .style('opacity', 1)
          .style('stroke-width', 2)
          .style('filter', 'drop-shadow(0px 2px 3px rgba(0,0,0,0.3))');
        
        // Highlight its label
        svg.select(`#label-${selectedBias}`).style('opacity', 1);
        svg.select(`#label-bg-${selectedBias}`).style('opacity', 1);
        
        // Highlight its category
        const categoryId = bias.category;
        svg.select(`#category-${categoryId}`)
          .style('opacity', 0.9);
        svg.select(`#label-${categoryId}`).style('opacity', 1);
        svg.select(`#label-bg-${categoryId}`).style('opacity', 1);
        
        // Highlight the connection to its category
        svg.select(`#link-${categoryId}-to-${selectedBias}`)
          .style('opacity', 0.8)
          .style('stroke-width', 2);
        
        // Highlight related biases and their connections
        if (bias.related && Array.isArray(bias.related)) {
          bias.related.forEach(relatedId => {
            // Get the related bias's data
            const relatedBias = allBiases.find(b => b.id === relatedId);
            if (!relatedBias) return;
            
            // Highlight the related bias node - slightly stronger than before
            svg.select(`#bias-${relatedId}`)
              .style('opacity', 0.9)
              .style('stroke-width', 1.5);
            
            // Highlight its label if it exists
            svg.select(`#label-${relatedId}`).style('opacity', 0.9);
            svg.select(`#label-bg-${relatedId}`).style('opacity', 0.9);
            
            // Highlight the connection to its category
            const relatedCategoryId = relatedBias.category;
            svg.select(`#link-${relatedCategoryId}-to-${relatedId}`)
              .style('opacity', 0.6);
            
            // Highlight the connection between biases
            svg.select(`#link-${selectedBias}-to-${relatedId}`)
              .style('opacity', 1)
              .style('stroke-width', 2)
              .style('stroke', '#666');
            svg.select(`#link-${relatedId}-to-${selectedBias}`)
              .style('opacity', 1)
              .style('stroke-width', 2)
              .style('stroke', '#666');
          });
        }
      }
    } else {
      // Show all categories and biases normally
      categories.forEach(category => {
        svg.select(`#category-${category.id}`)
          .style('opacity', 1)
          .style('stroke-width', 2);
        svg.select(`#label-${category.id}`).style('opacity', 1);
        svg.select(`#label-bg-${category.id}`).style('opacity', 1);
        svg.select(`#link-center-to-${category.id}`)
          .style('opacity', 0.6)
          .style('stroke-width', 3);
        
        // Show bias nodes for all categories
        svg.selectAll(`.${category.id}-bias`)
          .style('opacity', 0.8)
          .style('stroke-width', 1);
        svg.selectAll(`.${category.id}-label`).style('opacity', 1);
        svg.selectAll(`.${category.id}-label-bg`).style('opacity', 1);
        svg.selectAll(`.${category.id}-link`)
          .style('opacity', 0.6)
          .style('stroke-width', 1.5);
      });
      
      // Show related links
      svg.selectAll('.related-link').style('opacity', 0.3);
    }
    
    // Filter by search term (will highlight search results on top of any selection)
    if (searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      
      // Dim all bias nodes further - increased opacity reduction
      svg.selectAll('.bias-node').style('opacity', (opacity) => Math.min(0.15, parseFloat(opacity as string)));
      svg.selectAll('.bias-label').style('opacity', (opacity) => Math.min(0.15, parseFloat(opacity as string)));
      svg.selectAll('.bias-label-bg').style('opacity', (opacity) => Math.min(0.15, parseFloat(opacity as string)));
      
      // Show only matching bias nodes
      allBiases.forEach(bias => {
        if (bias.name.toLowerCase().includes(searchLower)) {
          svg.select(`#bias-${bias.id}`).style('opacity', 1);
          svg.select(`#label-${bias.id}`).style('opacity', 1);
          svg.select(`#label-bg-${bias.id}`).style('opacity', 1);
          
          // Show links to category
          svg.select(`#link-${bias.category}-to-${bias.id}`).style('opacity', 0.6);
          
          // Show category
          svg.select(`#category-${bias.category}`).style('opacity', 1);
          svg.select(`#label-${bias.category}`).style('opacity', 1);
          svg.select(`#label-bg-${bias.category}`).style('opacity', 1);
          svg.select(`#link-center-to-${bias.category}`).style('opacity', 0.6);
        }
      });
    }
    
    // Restore zoom transform after applying filters
    setTimeout(() => {
      g.attr('transform', `translate(${currentX}, ${currentY}) scale(${currentScale})`)
        .attr('data-zoom-scale', currentScale)
        .attr('data-zoom-x', currentX)
        .attr('data-zoom-y', currentY);
    }, 10);
  };
  
  // Initialize visualization
  useEffect(() => {
    if (isLoading || !svgRef.current) return;

    // Clear any existing content
    d3.select(svgRef.current).selectAll('*').remove();
    
    // Debug logging
    console.log('Initializing visualization with data:', { 
      biasesCount: allBiases.length, 
      categories: Object.keys(categoryNames),
      firstFewBiases: allBiases.slice(0, 3)
    });

    // Create tooltip if it doesn't exist
    if (!document.querySelector('.tooltip')) {
      d3.select('body').append('div')
        .attr('class', 'tooltip')
        .style('opacity', 0)
        .style('position', 'absolute')
        .style('background-color', 'white')
        .style('color', '#333')
        .style('border-radius', '5px')
        .style('padding', '10px')
        .style('box-shadow', '0 4px 14px rgba(0,0,0,0.15)')
        .style('pointer-events', 'none')
        .style('z-index', 1000)
        .style('font-size', '12px')
        .style('max-width', '220px')
        .style('transition', 'opacity 0.3s');
    }

    const tooltip = d3.select('.tooltip');
    
    // Set up SVG dimensions
    const svg = d3.select(svgRef.current);
    const container = svg.node()?.parentElement;
    const width = container ? container.clientWidth : 800;
    const height = container ? container.clientHeight : 600;
    const centerX = width / 2;
    const centerY = height / 2;

    // Function to adapt layout based on screen size
    const scaleFactor = Math.min(width, height) / 800;
    const categoryRadius = 200 * scaleFactor;
    const nodeSizeLarge = 30 * scaleFactor;
    const nodeSizeSmall = 12 * scaleFactor;
    const centerNodeSize = 35 * scaleFactor; // Reduced from 50 * scaleFactor
    
    // Create a group for all visualization elements
    const g = svg.append('g')
      .attr('class', 'everything');
    
    // Add background
    g.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'transparent')
      .attr('pointer-events', 'all');
    
    // Create a group for links
    const linksGroup = g.append('g')
      .attr('class', 'links');
    
    // Create a group for nodes
    const nodesGroup = g.append('g')
      .attr('class', 'nodes');
    
    // Draw central brain icon with background circle
    nodesGroup.append('circle')
      .attr('cx', centerX)
      .attr('cy', centerY)
      .attr('r', centerNodeSize * 1.05) // Reduced multiplier from 1.1 to 1.05
      .attr('fill', 'white')
      .attr('class', 'central-node-bg')
      .style('filter', 'drop-shadow(0px 1px 2px rgba(0,0,0,0.2))');

    nodesGroup.append('image')
      .attr('x', centerX - centerNodeSize)
      .attr('y', centerY - centerNodeSize)
      .attr('width', centerNodeSize * 2)
      .attr('height', centerNodeSize * 2)
      .attr('href', '/logo.webp')
      .attr('class', 'node central-node')
      .style('filter', 'drop-shadow(0px 1px 2px rgba(0,0,0,0.2))');
    
    // Create category groups
    const categories = Object.keys(categoryNames).map(id => ({
      id,
      name: categoryNames[id],
      color: categoryColors[id]
    }));
    
    const categoryNodes: CategoryNode[] = [];
    
    categories.forEach((category, i) => {
      const angle = (i / categories.length) * 2 * Math.PI;
      const x = centerX + Math.cos(angle) * categoryRadius;
      const y = centerY + Math.sin(angle) * categoryRadius;
      
      // Store category node position for later use
      categoryNodes.push({
        id: category.id,
        name: category.name,
        x: x,
        y: y,
        color: category.color
      });
      
      // Draw category node
      nodesGroup.append('circle')
        .attr('cx', x)
        .attr('cy', y)
        .attr('r', nodeSizeLarge)
        .attr('fill', category.color)
        .attr('stroke', '#FFFFFF')
        .attr('stroke-width', 2)
        .attr('class', 'node category-node')
        .attr('id', `category-${category.id}`)
        .style('filter', 'drop-shadow(0px 2px 3px rgba(0,0,0,0.2))')
        .on('mouseover', function(event) {
          // Apply hover effects and show tooltip regardless of selection state
          d3.select(this)
            .attr('r', nodeSizeLarge * 1.1)
            .style('filter', 'drop-shadow(0px 3px 5px rgba(0,0,0,0.3))');
          
          // Show tooltip
          tooltip
            .style('opacity', 1)
            .style('background-color', 'white')
            .style('box-shadow', '0 6px 18px rgba(0,0,0,0.25)')
            .style('border-left', `5px solid ${category.color}`)
            .style('padding', '12px')
            .style('border-radius', '6px')
            .html(`
              <div style="padding-left: 6px;">
                <strong style="font-size: 14px; color:${category.color}">${category.name} Biases</strong>
                <div style="font-size: 11px; margin-top: 4px;">Click to filter by this category</div>
              </div>
            `)
            .style('left', (event.pageX + 25) + 'px')
            .style('top', (event.pageY - 20) + 'px');
        })
        .on('mouseout', function() {
          // Restore normal appearance if not selected
          if (selectedCategory !== category.id) {
            d3.select(this)
              .attr('r', nodeSizeLarge)
              .style('filter', 'drop-shadow(0px 2px 3px rgba(0,0,0,0.2))');
          } else {
            // Keep styling but slightly reduce size if selected
            d3.select(this)
              .attr('r', nodeSizeLarge);
          }
          
          // Hide tooltip
          tooltip.style('opacity', 0);
        })
        .on('click', function() {
          // Handle selection changes when clicking on a category
          if (selectedCategory === category.id) {
            // Deselect current category
            setSelectedCategory('all');
          } else {
            // Select new category and clear any selected bias
            setSelectedCategory(category.id);
            setSelectedBias(null);
          }
          
          // Clear search term when selecting a category
          setSearchTerm('');
          
          // Hide tooltip when clicking
          tooltip.style('opacity', 0);
        });
      
      // Draw line from center to category
      linksGroup.append('line')
        .attr('x1', centerX)
        .attr('y1', centerY)
        .attr('x2', x)
        .attr('y2', y)
        .attr('stroke', category.color)
        .attr('stroke-width', 3)
        .attr('opacity', 0.6)
        .attr('class', 'link category-link')
        .attr('id', `link-center-to-${category.id}`);
      
      // Add category label with background for better readability
      const categoryLabelWidth = Math.max(category.name.length * 7.5 + 20, 60);
      nodesGroup.append('rect')
        .attr('x', x - categoryLabelWidth / 2)
        .attr('y', y - 10)
        .attr('width', categoryLabelWidth)
        .attr('height', 20)
        .attr('rx', 10)
        .attr('fill', 'rgba(255, 255, 255, 0.7)')
        .attr('stroke', category.color)
        .attr('stroke-width', 1.5)
        .attr('class', 'category-label-bg')
        .attr('id', `label-bg-${category.id}`);

      nodesGroup.append('text')
        .attr('x', x)
        .attr('y', y)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '12px')
        .attr('fill', '#333333')
        .attr('font-weight', 'bold')
        .attr('class', 'node-label category-label')
        .attr('id', `label-${category.id}`)
        .text(category.name);
    });
    
    // Position biases around their categories
    const biasNodes: BiasNode[] = [];
    
    categories.forEach((category: {id: string, name: string, color: string}, i: number) => {
      const categoryNode = categoryNodes.find(node => node.id === category.id);
      if (!categoryNode) return;
      
      const categoryBiases = allBiases.filter(bias => bias.category === category.id);
      console.log(`Category: ${category.id}, Found biases: ${categoryBiases.length}`);
      
      const biasRadius = 120 * scaleFactor; // Increase spacing from category center
      
      // Calculate how to distribute biases in a partial circle around the category
      categoryBiases.forEach((bias, j) => {
        // Create a more even distribution in a partial circle
        // Distribute between -120 degrees to +120 degrees from the category's position
        const angleRange = Math.PI * 0.8; // Wider range for better spread
        const startAngle = (i / categories.length) * 2 * Math.PI - (angleRange / 2);
        const biasAngle = startAngle + (j / (categoryBiases.length - 1 || 1)) * angleRange;
        
        // Add some random variation to prevent perfect overlap for large datasets
        const randomOffset = Math.random() * 0.1;
        // Increase the staggering to spread nodes more evenly
        const distanceVariation = (j % 4) * 30 * scaleFactor;
        const distance = biasRadius + distanceVariation; // Stagger distances for better distribution
        
        const biasX = categoryNode.x + Math.cos(biasAngle + randomOffset) * distance;
        const biasY = categoryNode.y + Math.sin(biasAngle + randomOffset) * distance;
        
        // Store bias node position for later use
        biasNodes.push({
          id: bias.id,
          name: bias.name,
          x: biasX,
          y: biasY,
          category: category.id,
          color: category.color
        });
        
        // Draw bias node
        nodesGroup.append('circle')
          .attr('cx', biasX)
          .attr('cy', biasY)
          .attr('r', nodeSizeSmall)
          .attr('fill', category.color)
          .attr('opacity', 0.8)
          .attr('stroke', '#FFFFFF')
          .attr('stroke-width', 1)
          .attr('class', `node bias-node ${category.id}-bias`)
          .attr('id', `bias-${bias.id}`)
          .on('mouseover', function(event) {
            // Apply hover effects and show tooltip regardless of selection state
            d3.select(this)
              .attr('r', nodeSizeSmall * 1.2)
              .attr('opacity', 1)
              .style('filter', 'drop-shadow(0px 2px 3px rgba(0,0,0,0.2))');
            
            // Find the category information
            const categoryColor = categoryColors[bias.category] || category.color;
            const categoryName = categoryNames[bias.category] || category.name;
            
            // Show tooltip with more info
            tooltip
              .style('opacity', 1)
              .style('background-color', 'white')
              .style('box-shadow', '0 6px 18px rgba(0,0,0,0.25)')
              .style('border-left', `5px solid ${categoryColor}`)
              .style('padding', '12px')
              .style('border-radius', '6px')
              .html(`
                <div style="padding-left: 6px;">
                  <strong style="font-size: 14px;">${bias.name}</strong>
                  <div style="font-size: 11px; color: #666; margin: 4px 0; font-weight: 500;">${categoryName} Bias</div>
                  <div style="font-size: 10px; margin-top: 8px; font-style: italic; text-align: left;">Click for more info</div>
            </div>
          `)
              .style('left', (event.pageX + 25) + 'px')
              .style('top', (event.pageY - 20) + 'px');
          
            // Highlight related biases only if no bias is currently selected
            if (!selectedBias && selectedCategory === 'all') {
              utilsRef.current.highlightRelatedBiases(bias.id);
      }
    })
    .on('mouseout', function() {
            // Restore normal appearance if not selected
            if (selectedBias !== bias.id) {
              d3.select(this)
                .attr('r', nodeSizeSmall)
                .attr('opacity', 0.8)
                .style('filter', null);
            } else {
              // Keep size and filter but reset opacity if selected
              d3.select(this)
                .attr('opacity', 1);
            }
            
      // Hide tooltip
            tooltip.style('opacity', 0);
            
            // Remove highlighting only if no bias is selected
            if (!selectedBias && selectedCategory === 'all') {
              utilsRef.current.resetHighlighting();
            }
          })
          .on('click', function() {
            // Handle selection changes when clicking on a bias
            if (selectedBias === bias.id) {
              // Deselect current bias
              setSelectedBias(null);
            } else {
              // Select new bias and clear any category filter
              setSelectedBias(bias.id);
              setSelectedCategory('all');
            }
            
            // Clear search term when selecting a bias directly from visualization
            setSearchTerm('');
            
            // Hide tooltip when clicking
            tooltip.style('opacity', 0);
          });
        
        // Draw line from category to bias
        linksGroup.append('line')
          .attr('x1', categoryNode.x)
          .attr('y1', categoryNode.y)
          .attr('x2', biasX)
          .attr('y2', biasY)
          .attr('stroke', category.color)
          .attr('stroke-width', 1.5)
          .attr('opacity', 0.6)
          .attr('class', `link bias-link ${category.id}-link`)
          .attr('id', `link-${category.id}-to-${bias.id}`);
        
        // Only add labels to nodes that aren't too crowded
        // Add labels to a subset of nodes to avoid clutter
        if (j % 4 === 0 || categoryBiases.length < 8) {
          // Calculate label width based on text length
          const labelWidth = Math.max(bias.name.length * 4.5 + 20, 60);
          const labelHeight = 18;
          const labelX = biasX - labelWidth / 2;
          const labelY = biasY + nodeSizeSmall + 8;

          // Background for bias label with position adjusted to avoid overlap
          nodesGroup.append('rect')
            .attr('x', labelX)
            .attr('y', labelY)
            .attr('width', labelWidth)
            .attr('height', labelHeight)
            .attr('rx', 8)
            .attr('fill', 'rgba(255, 255, 255, 0.9)')
            .attr('stroke', category.color)
            .attr('stroke-width', 0) // Changed from 0.5 to 0 to remove the outline
            .attr('class', `node-label-bg bias-label-bg ${category.id}-label-bg`)
            .attr('id', `label-bg-${bias.id}`);
          
          nodesGroup.append('text')
            .attr('x', biasX)
            .attr('y', labelY + labelHeight/2)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('font-size', '10px')
            .attr('font-weight', 'medium')
            .attr('fill', '#333333')
            .attr('class', `node-label bias-label ${category.id}-label`)
            .attr('id', `label-${bias.id}`)
            .text(bias.name);
        }
      });
    });
    
    // Draw connections between related biases
    allBiases.forEach(bias => {
      try {
        if (!bias.related || !Array.isArray(bias.related)) return;
        
        const sourceBiasNode = biasNodes.find(node => node.id === bias.id);
        if (!sourceBiasNode) return;
        
        bias.related.forEach(relatedId => {
          if (!relatedId) return;
          
          const targetBiasNode = biasNodes.find(node => node.id === relatedId);
          
          // Only draw if target node exists
          if (targetBiasNode) {
            linksGroup.append('line')
              .attr('x1', sourceBiasNode.x)
              .attr('y1', sourceBiasNode.y)
              .attr('x2', targetBiasNode.x)
              .attr('y2', targetBiasNode.y)
              .attr('stroke', '#999999')
              .attr('stroke-width', 1)
              .attr('opacity', 0.3)
              .attr('class', 'link related-link')
              .attr('id', `link-${bias.id}-to-${relatedId}`);
          }
        });
      } catch (error) {
        console.error('Error processing related biases for:', bias.name, error);
      }
    });
    
    // Apply filters when component mounts
    applyFilters();
    
    // Highlight related biases
    function highlightRelatedBiases(biasId: string) {
      // Find the bias
      const bias = allBiases.find(b => b.id === biasId);
      if (!bias || !bias.related) return;
      
      const svg = d3.select(svgRef.current);
      
      // Dim all nodes and links
      svg.selectAll('.bias-node').style('opacity', 0.2);
      svg.selectAll('.bias-label').style('opacity', 0.2);
      svg.selectAll('.bias-label-bg').style('opacity', 0.2);
      svg.selectAll('.category-node').style('opacity', 0.4);
      svg.selectAll('.category-label').style('opacity', 0.4);
      svg.selectAll('.category-label-bg').style('opacity', 0.4);
      svg.selectAll('.related-link').style('opacity', 0.05);
      svg.selectAll('.bias-link').style('opacity', 0.05);
      
      // Highlight the selected bias node
      svg.select(`#bias-${biasId}`)
        .style('opacity', 1)
        .style('stroke-width', 2)
        .style('filter', 'drop-shadow(0px 2px 3px rgba(0,0,0,0.3))');
      
      // Highlight its label
      svg.select(`#label-${biasId}`).style('opacity', 1);
      svg.select(`#label-bg-${biasId}`).style('opacity', 1);
      
      // Highlight its category
      const categoryId = bias.category;
      svg.select(`#category-${categoryId}`)
            .style('opacity', 0.9);
      svg.select(`#label-${categoryId}`).style('opacity', 1);
      svg.select(`#label-bg-${categoryId}`).style('opacity', 1);
      
      // Highlight the connection to its category
      svg.select(`#link-${categoryId}-to-${biasId}`)
        .style('opacity', 0.8)
        .style('stroke-width', 2);
      
      // Highlight related biases and their connections
      bias.related.forEach(relatedId => {
        // Get the related bias's data
        const relatedBias = allBiases.find(b => b.id === relatedId);
        if (!relatedBias) return;
        
        // Highlight the related bias node
        svg.select(`#bias-${relatedId}`)
          .style('opacity', 0.9)
          .style('stroke-width', 1.5);
        
        // Highlight its label if it exists
        svg.select(`#label-${relatedId}`).style('opacity', 0.9);
        svg.select(`#label-bg-${relatedId}`).style('opacity', 0.9);
        
        // Highlight the connection to its category
        const relatedCategoryId = relatedBias.category;
        svg.select(`#link-${relatedCategoryId}-to-${relatedId}`)
          .style('opacity', 0.6);
        
        // Highlight its category
        svg.select(`#category-${relatedCategoryId}`)
          .style('opacity', 0.8);
          
        // Highlight the connection between biases
        svg.select(`#link-${biasId}-to-${relatedId}`)
          .style('opacity', 1)
          .style('stroke-width', 2)
          .style('stroke', '#666');
        svg.select(`#link-${relatedId}-to-${biasId}`)
          .style('opacity', 1)
          .style('stroke-width', 2)
          .style('stroke', '#666');
      });
    }
    
    // Reset highlighting
    function resetHighlighting() {
      const svg = d3.select(svgRef.current);
      
      // Remove all custom styling
      svg.selectAll('.bias-node')
        .style('stroke-width', 1)
        .style('filter', null);
      svg.selectAll('.related-link')
        .style('stroke-width', 1)
        .style('stroke', '#999999');
      
      // Apply the standard filter state
      applyFilters();
    }

    // Store references to the functions so they can be used outside this useEffect
    utilsRef.current = {
      highlightRelatedBiases,
      resetHighlighting
    };
    
  }, [allBiases, isLoading, selectedCategory, searchTerm, selectedBias]);
  
  // Re-apply filters whenever selection state changes
  useEffect(() => {
    if (!svgRef.current) return;
    
    // Apply filters whenever selection state changes
    const timer = setTimeout(() => {
      applyFilters();
    }, 50);
    
    return () => clearTimeout(timer);
  }, [selectedCategory, selectedBias, searchTerm]);
  
  // Update SVG transform based on zoom state
  useEffect(() => {
    if (!svgRef.current) return;
    
    const g = d3.select(svgRef.current).select('.everything');
    if (!g.empty()) {
      // Save the current transform to data attributes
      g.attr('data-zoom-scale', zoomScale)
       .attr('data-zoom-x', zoomTranslate.x)
       .attr('data-zoom-y', zoomTranslate.y)
       .attr('transform', `translate(${zoomTranslate.x}, ${zoomTranslate.y}) scale(${zoomScale})`);
       
      // Log for debugging
      console.log(`Applied zoom: scale=${zoomScale}, x=${zoomTranslate.x}, y=${zoomTranslate.y}`);
    }
  }, [zoomScale, zoomTranslate]);

  // Re-apply zoom transform after filters update to prevent zoom reset
  useEffect(() => {
    if (!svgRef.current) return;
    
    const g = d3.select(svgRef.current).select('.everything');
    if (!g.empty()) {
      // After filter changes, ensure the zoom level stays the same
      const storedScale = g.attr('data-zoom-scale');
      const storedX = g.attr('data-zoom-x');
      const storedY = g.attr('data-zoom-y');
      
      if (storedScale && storedX && storedY) {
        // Apply saved transform immediately and after a short delay to ensure it works
        g.attr('transform', `translate(${storedX}, ${storedY}) scale(${storedScale})`);
        
        // Apply again after a short delay to handle any race conditions
        setTimeout(() => {
          g.attr('transform', `translate(${storedX}, ${storedY}) scale(${storedScale})`);
        }, 50);
        
        console.log(`Restored zoom after filter change: scale=${storedScale}, x=${storedX}, y=${storedY}`);
      }
    }
  }, [selectedCategory, selectedBias, searchTerm]);

  // Add wheel event for zooming
  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      if (!containerRef.current) return;
      event.preventDefault();
      
      // Adjust scale based on wheel direction
      const delta = event.deltaY < 0 ? 1.1 : 0.9;
      const newScale = Math.max(0.1, Math.min(5, zoomScale * delta));
      
      // Get center coordinates
      const centerX = (containerRef.current.clientWidth || 0) / 2;
      const centerY = (containerRef.current.clientHeight || 0) / 2;
      
      // Calculate how much the center point would move
      const currentCenterX = centerX - zoomTranslate.x;
      const currentCenterY = centerY - zoomTranslate.y;
      
      // Scale from the center point
      const newTranslateX = centerX - currentCenterX * (newScale / zoomScale);
      const newTranslateY = centerY - currentCenterY * (newScale / zoomScale);
      
      // Update zoom state
      setZoomScale(newScale);
      setZoomTranslate({ x: newTranslateX, y: newTranslateY });
      
      // Immediately apply transform to prevent flickering
      const g = d3.select(svgRef.current).select('.everything');
      if (!g.empty()) {
        g.attr('data-zoom-scale', newScale)
         .attr('data-zoom-x', newTranslateX)
         .attr('data-zoom-y', newTranslateY)
         .attr('transform', `translate(${newTranslateX}, ${newTranslateY}) scale(${newScale})`);
      }
    };
    
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
    }
    
    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [zoomScale, zoomTranslate, svgRef]);

  // Reset zoom and pan
  const handleResetView = () => {
    setZoomScale(1);
    setZoomTranslate({ x: 0, y: 0 });
  };

  // Reset only filters without affecting zoom
  const resetFilters = () => {
    setSelectedCategory('all');
    setSearchTerm('');
    setSelectedBias(null);
  };

  // Reset both zoom and filters
  const resetAll = () => {
    handleResetView();
    resetFilters();
  };

  // Handle pan with mouse drag
  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;
    
    const startX = event.clientX;
    const startY = event.clientY;
    const startTranslateX = zoomTranslate.x;
    const startTranslateY = zoomTranslate.y;
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      setZoomTranslate({
        x: startTranslateX + dx,
        y: startTranslateY + dy
      });
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  // Filter by category
  useEffect(() => {
    const categoryButtons = document.querySelectorAll('.category-filter');
    categoryButtons.forEach(button => {
      button.classList.remove('active');
      if ((button as HTMLElement).dataset.category === selectedCategory) {
        button.classList.add('active');
      }
    });
  }, [selectedCategory]);
  
  // Get the selected bias data
  const getSelectedBiasData = () => {
    if (!selectedBias) return null;
    return allBiases.find(bias => bias.id === selectedBias);
  };
  
  // Get related biases
  const getRelatedBiases = () => {
    const selectedBiasData = getSelectedBiasData();
    if (!selectedBiasData || !selectedBiasData.related) return [];
    
    return allBiases.filter(bias => 
      selectedBiasData.related?.includes(bias.id)
    );
  };

  // Alias resetVisualization to resetFilters to preserve zoom on reset
  const resetVisualization = resetFilters;

  // Handle bias selection from SearchInput
  const handleBiasSelect = (bias: CognitiveBias) => {
    try {
      if (!bias || !bias.id) {
        console.warn("Invalid bias object received from SearchInput:", bias);
        return;
      }
      
      console.log("Selected bias from search:", bias);
      
      // Clear previous selections and select the new bias
      setSelectedCategory('all');
      setSelectedBias(bias.id);
      
      // Only update search term if it's provided
      if (bias.name) {
        setSearchTerm(bias.name);
      }
      
      // If we have access to the visualization, highlight the node and show info
      if (svgRef.current && containerRef.current) {
        const biasNode = d3.select(svgRef.current).select(`#bias-${bias.id}`);
        if (!biasNode.empty()) {
          // Get the node element and position
          const biasElement = biasNode.node();
          const tooltip = d3.select('.tooltip');
          
          if (biasElement) {
            // Find the category and related information for this bias
            const biasData = allBiases.find(b => b.id === bias.id);
            if (biasData) {
              const categoryColor = categoryColors[biasData.category] || "#999";
              const categoryName = categoryNames[biasData.category] || "Uncategorized";
              
              // Get container dimensions for centered positioning
              const containerRect = containerRef.current.getBoundingClientRect();
              
              // Position tooltip at a fixed position in the center of the container
              tooltip
                .style('opacity', 1)
                .style('background-color', 'white')
                .style('box-shadow', '0 6px 18px rgba(0,0,0,0.25)')
                .style('border-left', `5px solid ${categoryColor}`)
                .style('padding', '12px')
                .style('border-radius', '6px')
                .html(`
                  <div style="padding-left: 6px;">
                    <strong style="font-size: 14px;">${biasData.name}</strong>
                    <div style="font-size: 11px; color: #666; margin: 4px 0; font-weight: 500;">${categoryName} Bias</div>
                    <div style="font-size: 10px; margin-top: 8px; font-style: italic; text-align: left;">Click for more info</div>
      </div>
                `)
                .style('left', `${window.scrollX + containerRect.left + (containerRect.width / 2) - 100}px`)
                .style('top', `${window.scrollY + containerRect.top + 100}px`)
                .style('max-width', '250px')
                .style('z-index', '1000');
              
              // Set up a timeout to hide the tooltip after a few seconds
              setTimeout(() => {
                tooltip.style('opacity', 0);
              }, 3000); // Reduced to 3 seconds
            }
          }
        }
      }
    } catch (error) {
      console.error("Error selecting bias from search:", error);
    }
  };

  // Get the selected category data
  const getSelectedCategoryData = () => {
    if (selectedCategory === 'all') return null;
    return {
      id: selectedCategory,
      name: categoryNames[selectedCategory] || "Unknown Category",
      description: categoryDescriptions[selectedCategory] || "No description available",
      color: categoryColors[selectedCategory] || "#999999"
    };
  };
  
  // Get biases for the selected category
  const getCategoryBiases = () => {
    if (selectedCategory === 'all') return [];
    return allBiases.filter(bias => bias.category === selectedCategory);
  };

  // Handle bias selection from sidebar
  const handleBiasSelectFromSidebar = (biasId: string) => {
    // First, deselect the category to show the whole graph
    setSelectedCategory('all');
    
    // Find the bias node in the visualization
    if (svgRef.current) {
      const biasNode = d3.select(svgRef.current).select(`#bias-${biasId}`);
      
      if (!biasNode.empty()) {
        // Get the node's position
        const biasElement = biasNode.node() as SVGCircleElement;
        if (biasElement) {
          const centerX = (containerRef.current?.clientWidth || 0) / 2;
          const centerY = (containerRef.current?.clientHeight || 0) / 2;
          
          // Calculate the new translation to center on the selected bias
          const cx = parseFloat(biasElement.getAttribute('cx') || '0');
          const cy = parseFloat(biasElement.getAttribute('cy') || '0');
          
          // Center the view on the selected bias with the current zoom level
          const newTranslateX = centerX - cx * zoomScale;
          const newTranslateY = centerY - cy * zoomScale;
          
          // Animate the transition to center the bias
          d3.select(svgRef.current).select('.everything')
            .transition()
            .duration(500)
            .attr('transform', `translate(${newTranslateX}, ${newTranslateY}) scale(${zoomScale})`)
            .attr('data-zoom-scale', zoomScale)
            .attr('data-zoom-x', newTranslateX)
            .attr('data-zoom-y', newTranslateY);
          
          // Update the zoom state
          setZoomTranslate({ x: newTranslateX, y: newTranslateY });
        }
      }
    }
    
    // Select the bias (after a small delay to ensure category filter is cleared first)
    setTimeout(() => {
      setSelectedBias(biasId);
    }, 50);
  };

  return (
    <div className="flex flex-col lg:flex-row h-full gap-4 relative">
      <div className={`transition-all duration-300 ease-in-out ${sidebarVisible ? 'lg:w-3/4' : 'lg:w-[95%]'} bg-white/90 p-4 rounded-xl shadow-md backdrop-blur-sm relative`}>
        {/* Header and controls */}
        <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
          <div className="w-full sm:w-96">
            <SearchInput 
              biases={allBiases} 
              onBiasSelect={handleBiasSelect} 
            />
            </div>
            
          <div className="flex flex-wrap gap-2 w-full sm:w-auto justify-start sm:justify-end">
                <button
              className="px-3 py-2 rounded-full text-sm font-medium bg-gray-200/80 hover:bg-gray-300 text-gray-700 flex items-center gap-1"
                  onClick={() => {
                // Zoom in while keeping center point fixed
                const newScale = zoomScale * 1.2;
                const centerX = (containerRef.current?.clientWidth || 0) / 2;
                const centerY = (containerRef.current?.clientHeight || 0) / 2;
                
                // Calculate how much the center point would move
                const currentCenterX = centerX - zoomTranslate.x;
                const currentCenterY = centerY - zoomTranslate.y;
                
                // Scale from the center point
                const newTranslateX = centerX - currentCenterX * (newScale / zoomScale);
                const newTranslateY = centerY - currentCenterY * (newScale / zoomScale);
                
                setZoomScale(newScale);
                setZoomTranslate({ x: newTranslateX, y: newTranslateY });
              }}
            >
              <ZoomIn className="w-3.5 h-3.5" /> Zoom In
                </button>
                <button
              className="px-3 py-2 rounded-full text-sm font-medium bg-gray-200/80 hover:bg-gray-300 text-gray-700 flex items-center gap-1"
                  onClick={() => {
                // Zoom out while keeping center point fixed
                const newScale = Math.max(0.5, zoomScale / 1.2);
                const centerX = (containerRef.current?.clientWidth || 0) / 2;
                const centerY = (containerRef.current?.clientHeight || 0) / 2;
                
                // Calculate how much the center point would move
                const currentCenterX = centerX - zoomTranslate.x;
                const currentCenterY = centerY - zoomTranslate.y;
                
                // Scale from the center point
                const newTranslateX = centerX - currentCenterX * (newScale / zoomScale);
                const newTranslateY = centerY - currentCenterY * (newScale / zoomScale);
                
                setZoomScale(newScale);
                setZoomTranslate({ x: newTranslateX, y: newTranslateY });
              }}
            >
              <ZoomOut className="w-3.5 h-3.5" /> Zoom Out
                </button>
                <button
              className="px-3 py-2 rounded-full text-sm font-medium bg-gray-200/80 hover:bg-gray-300 text-gray-700 flex items-center gap-1"
              onClick={resetAll}
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset All
                </button>
        </div>
      </div>
      
        {/* Visualization */}
        <div 
          ref={containerRef}
          className="relative w-full h-[500px] md:h-[600px] lg:h-[700px] overflow-hidden cursor-grab relative rounded-lg shadow-inner bg-slate-50/50"
          onMouseDown={handleMouseDown}
        >
          <svg 
            ref={svgRef} 
            key={`${selectedCategory}-${selectedBias}-${searchTerm}-${allBiases.length}`}
            width="100%" 
            height="100%" 
            className="select-none"
          ></svg>
        </div>
      </div>
      
      {/* Info panel with sliding animation */}
      <div 
        className={`transition-all duration-300 ease-in-out ${
          sidebarVisible ? 'lg:w-1/4 lg:max-w-[320px] lg:min-w-[280px] opacity-100 visible translate-x-0' : 
          'lg:w-[5%] lg:max-w-[50px] lg:min-w-[50px] lg:opacity-100 lg:visible lg:translate-x-0 opacity-0 invisible translate-x-full'
        } bg-white/90 rounded-xl shadow-md backdrop-blur-sm overflow-hidden relative`}
      >
        {/* Sidebar header with toggle button */}
        <div className={`${sidebarVisible ? 'flex items-center justify-between p-4 border-b border-gray-100' : 'flex justify-center p-4'}`}>
          <h2 className={`text-xl font-bold bg-gradient-to-r from-orange-600 via-pink-600 to-red-600 bg-clip-text text-transparent ${sidebarVisible ? 'opacity-100' : 'opacity-0 w-0 absolute'} transition-opacity duration-200`}>
            Cognitive Bias Map
          </h2>
          
          {/* Toggle button inside the sidebar */}
              <button
            onClick={() => setSidebarVisible(!sidebarVisible)}
            className="z-10 bg-gray-100 hover:bg-gray-200 shadow-sm rounded-md p-1.5 transition-all text-gray-600 hover:text-gray-800"
            aria-label={sidebarVisible ? "Hide sidebar" : "Show sidebar"}
          >
            {sidebarVisible ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            )}
              </button>
            </div>
            
        {/* Sidebar content */}
        <div className={`p-4 ${sidebarVisible ? 'opacity-100 visible' : 'opacity-0 invisible lg:hidden'} transition-opacity duration-200`}>
          {!selectedBias && selectedCategory === 'all' ? (
            <div>
              <p className="text-gray-700 mb-4">
                Explore the relationships between different cognitive biases and their categories in an interactive visualization. Click on any bias to learn more.
              </p>
              <p className="text-gray-600 mb-2 text-sm">
                <strong>Instructions:</strong>
              </p>
              <ul className="space-y-2 text-gray-600 text-sm mb-4">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                  <span>Click on bias nodes to see details</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                  <span>Use category buttons to filter by type</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                  <span>Hover over nodes to see connections</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                  <span>Scroll to zoom in and out</span>
                </li>
              </ul>
            </div>
          ) : selectedBias ? (
            <div>
              <h3 className="text-lg font-bold mb-1">{getSelectedBiasData()?.name}</h3>
              {getSelectedBiasData()?.category && (
                      <span 
                  className="inline-block px-2 py-1 rounded-full text-xs font-medium text-white mb-3"
                  style={{ backgroundColor: categoryColors[getSelectedBiasData()?.category || ''] }}
                >
                  {categoryNames[getSelectedBiasData()?.category || '']}
                </span>
              )}
              <p className="text-gray-600 mb-4 text-sm">{getSelectedBiasData()?.short_description}</p>
              {getRelatedBiases().length > 0 && (
                <div className="mb-4">
                  <h4 className="font-bold mb-2 text-sm text-gray-700">Related Biases:</h4>
                  <ul className="space-y-1 text-sm">
                    {getRelatedBiases().map(related => (
                      <li 
                        key={related.id}
                        className="cursor-pointer hover:text-orange-500 flex items-center gap-1"
                        onClick={() => setSelectedBias(related.id)}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: categoryColors[related.category] }}></span>
                        {related.name}
                    </li>
                  ))}
                </ul>
                </div>
            )}
                
                    <Link
                to={`/bias/${getSelectedBiasData()?.id}/`}
                className="inline-block mt-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-md hover:opacity-90 transition-opacity text-sm font-medium"
                onClick={() => {
                  // Scroll to top when the link is clicked
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }}
                    >
                View Details
                    </Link>
          </div>
          ) : selectedCategory !== 'all' ? (
          <div>
              <h3 className="text-lg font-bold mb-2" 
                  style={{ color: getSelectedCategoryData()?.color }}>
                {getSelectedCategoryData()?.name} Biases
              </h3>
              
              <p className="text-gray-700 mb-4 text-sm">{getSelectedCategoryData()?.description}</p>
              
              <div className="mb-4">
                <h4 className="font-bold mb-2 text-sm text-gray-700">
                  Biases in this category ({getCategoryBiases().length}):
                </h4>
                <ul className="space-y-1 text-sm max-h-40 overflow-y-auto pr-1">
                  {getCategoryBiases().map(bias => (
                    <li 
                      key={bias.id}
                      className="cursor-pointer hover:text-orange-500 flex items-center gap-1"
                      onClick={() => handleBiasSelectFromSidebar(bias.id)}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" 
                            style={{ backgroundColor: categoryColors[bias.category] }}></span>
                      {bias.name}
                </li>
                  ))}
              </ul>
                </div>
            
              <button
                onClick={() => setSelectedCategory('all')}
                className="inline-block mt-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors text-sm font-medium"
              >
                Show All Categories
              </button>
            </div>
          ) : null}
          
          <div className="mt-6 pt-4 border-t border-gray-100">
            <h4 className="font-bold mb-2 text-sm text-gray-700">Categories:</h4>
            <ul className="space-y-1.5">
              {Object.entries(categoryNames).map(([id, name]) => (
                <li key={id} className="flex items-center text-sm">
                  <button 
                    className={`flex items-center w-full hover:bg-gray-100 px-3 py-2 rounded-md transition-all duration-150 ${
                      selectedCategory === id 
                        ? 'bg-gray-100 font-semibold shadow-sm' 
                        : 'bg-gray-50/50'
                    }`}
                    onClick={() => {
                      setSelectedCategory(id);
                      setSelectedBias(null); // Clear selected bias when a category is selected
                    }}
                    title={categoryDescriptions[id]}
                  >
                    <span 
                      className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                      style={{ backgroundColor: categoryColors[id] }}
                    ></span>
                    <span className="text-gray-700 flex-grow">{name}</span>
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className="h-4 w-4 text-gray-400 ml-1 opacity-60" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  </li>
                ))}
              </ul>
              </div>
            </div>
      </div>
    </div>
  );
};

export default BiasNetworkGraph; 